import type { MouseEvent, ReactNode } from 'react';

import { Pencil, Trash2 } from 'lucide-react';
import { alpha, Avatar, Button, Stack, Typography } from '@mui/material';

import { stringToColor } from '@utils/stringToColor';

type GridPrimaryTextProps = {
  children: ReactNode;
};

export const GridPrimaryText = ({ children }: GridPrimaryTextProps) => (
  <Typography
    variant="body2"
    noWrap
    sx={{ fontWeight: 600, color: 'text.primary' }}
  >
    {children}
  </Typography>
);

type GridSecondaryTextProps = {
  value?: string | null;
};

export const GridSecondaryText = ({ value }: GridSecondaryTextProps) => (
  <Typography
    variant="body2"
    noWrap
    sx={{ color: value ? 'text.secondary' : 'text.disabled' }}
  >
    {value || '—'}
  </Typography>
);

type GridCountCellProps = {
  value?: number | null;
};

export const GridCountCell = ({ value }: GridCountCellProps) => (
  <Typography
    component="span"
    variant="body2"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 28,
      height: 24,
      px: 1,
      borderRadius: 999,
      bgcolor: 'action.hover',
      color: 'text.primary',
      fontWeight: 600,
      lineHeight: 1,
    }}
  >
    {value ?? 0}
  </Typography>
);

type GridPersonCellProps = {
  firstName?: string | null;
  lastName?: string | null;
};

export const GridPersonCell = ({
  firstName,
  lastName,
}: GridPersonCellProps) => {
  const name = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
  const initials =
    `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';

  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{ minWidth: 0 }}
    >
      <Avatar
        sx={{
          width: 28,
          height: 28,
          fontSize: '0.75rem',
          fontWeight: 600,
          bgcolor: stringToColor(name),
        }}
      >
        {initials}
      </Avatar>
      <Typography variant="body2" noWrap>
        {name}
      </Typography>
    </Stack>
  );
};

type GridRowActionsProps = {
  onEdit: () => void;
  onDelete: () => void;
  editLabel?: string;
  deleteLabel?: string;
};

const stopRowClick = (event: MouseEvent) => {
  event.stopPropagation();
};

export const GridRowActions = ({
  onEdit,
  onDelete,
  editLabel = 'Edit',
  deleteLabel = 'Delete',
}: GridRowActionsProps) => (
  <Stack
    direction="row"
    spacing={0.5}
    alignItems="center"
    onClick={stopRowClick}
  >
    <Button
      size="small"
      variant="text"
      startIcon={<Pencil size={15} />}
      onClick={onEdit}
      sx={{
        minWidth: 0,
        px: 1,
        color: 'text.secondary',
        '&:hover': {
          color: 'primary.main',
          backgroundColor: 'action.hover',
        },
      }}
    >
      {editLabel}
    </Button>
    <Button
      size="small"
      variant="text"
      color="error"
      startIcon={<Trash2 size={15} />}
      onClick={onDelete}
      sx={(theme) => ({
        minWidth: 0,
        px: 1,
        '&:hover': {
          backgroundColor: alpha(theme.palette.error.main, 0.08),
        },
      })}
    >
      {deleteLabel}
    </Button>
  </Stack>
);
